import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useImperativeHandle,
} from 'react';

import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  InitialTableState,
  Row,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import {
  useVirtualizer,
  VirtualItem,
  Virtualizer,
} from '@tanstack/react-virtual';

import { classNames } from '@/utils';
import { usePersistedJsonState } from '@superfunnel/react-hooks';
import Button from '@superfunnel/ui-core/v2/Button';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Popover, PopoverAnchor } from '@superfunnel/ui-core/v2/popover';
import {
  ArrowSeparateVertical,
  EyeClosed,
  ListSelect,
  Minus,
  MoreVert,
  NavArrowDown,
  NavArrowUp,
} from 'iconoir-react';
import { ColumnManagementPopoverContent } from '../DynamicPages/ColumnManagementPopover';
import './CustomDataGrid.css';
import { getCommonPinningStyles } from './utils';

const AnimateCell = () => (
  <div className="h-2.5 w-1/2 max-w-12 min-w-7 animate-pulse rounded-md bg-gray-300"></div>
);

type RowClassName<T> = string | ((row: Row<T>) => string);
type CellClassName<T> =
  | string
  | ((cell: Cell<T, unknown>, row: Row<T>) => string);

export interface CustomDataGridHandle<T> {
  table: Table<T>;
}

interface Props<T> {
  logicalName: string;
  columns: ColumnDef<T>[];
  data?: T[];
  initialState?: InitialTableState;
  tableClassName?: string;
  tableHeadClassName?: string;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  virtualized?: boolean;
  onReachedEnd?: () => void;

  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: T[]) => void;

  enableSorting?: boolean;
}

export const CustomDataGrid = forwardRef<CustomDataGridHandle<any>, Props<any>>(
  function CustomDataGrid<T>(
    {
      logicalName,
      data,
      columns,
      initialState,
      tableClassName: tableClassName,
      tableHeadClassName,
      tableBodyClassName,
      rowClassName: rowClassName,
      cellClassName: cellClassName,
      onRowClick,
      onCellClick,
      virtualized = true,
      onReachedEnd,
      enableRowSelection,
      onRowSelectionChange,
      enableSorting,
    }: Props<T>,
    ref: React.Ref<CustomDataGridHandle<T>>,
  ) {
    const [cachedInitialState, setCachedInitialState] = usePersistedJsonState<
      InitialTableState | undefined
    >('custom-data-grid-state:' + logicalName + ':' + window.location.pathname);
    const [, setRowSelection] = React.useState<Record<string, boolean>>({});

    const _onCellClick = useCallback(
      (cell: Cell<T, unknown>, e: MouseEvent) => {
        // to avoid a situation where a user accisentally clicks near a checkbox
        if (cell.column.id === 'select') {
          e.stopPropagation();
          e.preventDefault();
          cell.row.toggleSelected();
          return;
        }
        onCellClick?.(cell, e);
      },
      [onCellClick],
    );

    const _data: T[] =
      data ||
      ([
        { id: 'loading-item-1' },
        { id: 'loading-item-2' },
        { id: 'loading-item-3' },
        { id: 'loading-item-4' },
        { id: 'loading-item-5' },
      ] as any);

    // Validate column IDs are valid CSS identifiers (used in CSS variables for sizing).
    // Invalid IDs (containing spaces, special chars) silently break column widths.
    if (process.env.NODE_ENV !== 'production') {
      const CSS_SAFE_ID = /^[a-zA-Z0-9_-]+$/;
      for (const col of columns) {
        const id = (col as { id?: string }).id;
        if (id && !CSS_SAFE_ID.test(id)) {
          throw new Error(
            `[CustomDataGrid] Column id "${id}" contains characters invalid for CSS variable names. ` +
              'Use only alphanumeric characters, hyphens and underscores.',
          );
        }
      }
    }

    const table = useReactTable<T>({
      data: _data,
      columns: [
        ...(enableRowSelection
          ? ([
              {
                id: 'select',
                size: 48,
                minSize: 48,
                header: ({ table }) => (
                  <Checkbox
                    checked={
                      table.getIsSomeRowsSelected() ||
                      table.getIsAllRowsSelected()
                    }
                    customIcon={
                      table.getIsSomeRowsSelected() &&
                      !table.getIsAllRowsSelected() ? (
                        <Minus
                          className="h-3 w-3 font-medium"
                          strokeWidth={2.5}
                        />
                      ) : undefined
                    }
                    onCheckedChange={(checked) => {
                      table.getToggleAllRowsSelectedHandler()({
                        target: { checked },
                      } as any);
                    }}
                  />
                ),
                cell: ({ row }) => (
                  <Checkbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onCheckedChange={(checked) => {
                      row.toggleSelected(!!checked);
                    }}
                  />
                ),
              },
            ] satisfies ColumnDef<T>[])
          : []),
        ...columns,
      ],
      defaultColumn: {
        minSize: 60,
        maxSize: 800,
      },
      columnResizeMode: 'onChange',
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      // debugTable: true,
      // debugHeaders: true,
      // debugColumns: true,
      enableRowSelection: enableRowSelection,
      onRowSelectionChange: (updater) => {
        if (!updater) return;

        setRowSelection((old) => {
          const newState = updater instanceof Function ? updater(old) : updater;
          table.setState((s) => {
            s.rowSelection = newState;
            return s;
          });

          return newState;
        });

        setTimeout(() => {
          const rows = table
            .getRowModel()
            .rows.filter((row) => row.getIsSelected());
          onRowSelectionChange?.(rows.map((row) => row.original));
        }, 0);
      },
      enableColumnPinning: true,
      enableSorting: enableSorting ?? false,
      initialState: {
        ...cachedInitialState,
        ...initialState,
        columnVisibility: {
          ...initialState?.columnVisibility,
          ...cachedInitialState?.columnVisibility,
        },
        columnSizing: {
          ...initialState?.columnSizing,
          ...cachedInitialState?.columnSizing,
        },
      },
      onStateChange: (updater) => {
        if (!updater) return;

        setCachedInitialState((old) => {
          try {
            const newState =
              updater instanceof Function
                ? updater((old || {}) as any)
                : updater;

            return {
              columnOrder: newState.columnOrder,
              columnSizing: newState.columnSizing,
              columnVisibility: newState.columnVisibility,
            };
          } catch (_) {
            return old;
          }
        });
      },
    });

    const [manageColumnsOpen, setManageColumnsOpen] = React.useState<
      { open: true; headerId: string } | false
    >(false);

    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
      count: _data?.length ?? 0,
      estimateSize: () => 72, // estimate row height for accurate scrollbar dragging
      getScrollElement: () => tableContainerRef.current,
      // measure dynamic row height, except in firefox because it measures table border height incorrectly
      measureElement:
        typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
          ? (element) => element?.getBoundingClientRect().height
          : undefined,
      overscan: 5,
      enabled: virtualized,
    });

    /**
     * Instead of calling `column.getSize()` on every render for every header
     * and especially every data cell (very expensive),
     * we will calculate all column sizes at once at the root table level in a useMemo
     * and pass the column sizes down as CSS variables to the <table> element.
     */
    const columnSizeVars = React.useMemo(() => {
      const headers = table.getFlatHeaders();
      const colSizes: { [key: string]: number } = {};
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;
        colSizes[`--header-${header.id}-size`] = header.getSize();
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
      }
      return colSizes;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnSizingInfo,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnSizing,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      table.getState().columnVisibility,
      columns,
    ]);

    // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
    const onScrolled = React.useCallback(
      (containerRefElement?: HTMLDivElement | null) => {
        if (containerRefElement) {
          const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
          // once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
          if (scrollHeight - scrollTop - clientHeight < 500) {
            onReachedEnd?.();
          }
        }
      },
      [onReachedEnd],
    );

    // a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
    React.useEffect(() => {
      onScrolled(tableContainerRef.current);
    }, [onScrolled]);

    useImperativeHandle(ref, () => ({
      table,
    }));

    // function setDuplicateDynamicPageModal(arg0: { dynamicPage: any }) {
    //   throw new Error('Function not implemented.');
    // }

    return (
      <div
        className={classNames(
          virtualized && 'absolute inset-0 h-full',
          'block w-full overflow-auto',
        )}
        ref={tableContainerRef}
        onScroll={(e) => onScrolled(e.currentTarget)}
      >
        <div
          className={classNames('divTable relative min-w-full', tableClassName)}
          style={{
            ...columnSizeVars, // Define column sizes on the <table> element
            width: table.getTotalSize(),
          }}
        >
          <div className={classNames('thead', tableHeadClassName, 'bg-white')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id} className="tr bg-base-100 rounded-lg">
                {headerGroup.headers.map((header) => (
                  <div
                    key={header.id}
                    className={classNames(
                      'th group/sort text-base-600 flex items-center border-0 py-0 ps-5 pr-3 text-left text-sm font-semibold',
                      header.column?.getIsPinned() ? 'bg-base-100' : undefined,
                      header.column?.getIsPinned() === 'right' &&
                        header.column?.getIsLastColumn('right')
                        ? 'rounded-e-lg'
                        : undefined,
                      header.column?.getIsPinned() === 'left' &&
                        header.column?.getIsFirstColumn('left')
                        ? 'rounded-s-lg'
                        : undefined,
                    )}
                    style={{
                      ...(header.column?.getIsPinned()
                        ? getCommonPinningStyles('header', header.column as any)
                        : {}),
                      width: `calc(var(--header-${header?.id}-size) * 1px)`,
                    }}
                  >
                    <div className="-my-2 flex flex-1 shrink flex-row items-center gap-1 truncate py-2">
                      <span className="text-base-600 flex items-center truncate text-sm font-medium">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </span>
                      {header.column.getCanSort() &&
                        !header.column.getIsResizing() && (
                          <Button
                            variant="ghost"
                            size={'icon'}
                            className={classNames(
                              !header.column.getIsSorted()
                                ? 'text-base-200 hidden group-hover/sort:block'
                                : 'text-base-600',
                            )}
                            onClick={() => header.column.toggleSorting()}
                          >
                            {header.column.getIsSorted() === 'asc' ? (
                              <NavArrowUp className="h-4 w-4" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <NavArrowDown className="h-4 w-4" />
                            ) : (
                              <ArrowSeparateVertical className="h-4 w-4 text-gray-600" />
                            )}
                          </Button>
                        )}
                    </div>

                    {!header.column.getIsResizing() &&
                      header.id !== 'select' &&
                      header.column.getCanHide() && (
                        <Popover
                          modal={false}
                          open={
                            manageColumnsOpen
                              ? manageColumnsOpen.headerId === header.id
                              : false
                          }
                          onOpenChange={(open) =>
                            !open && setManageColumnsOpen(false)
                          }
                        >
                          <PopoverAnchor>
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                asChild
                                onClick={(e) => {
                                  if (manageColumnsOpen) {
                                    setManageColumnsOpen(false);
                                    e.stopPropagation();
                                  }
                                }}
                                className={classNames(
                                  'w-0 opacity-0 transition-none group-hover:w-auto group-hover:opacity-100 data-[state=open]:w-auto data-[state=open]:opacity-100',
                                  manageColumnsOpen &&
                                    manageColumnsOpen.headerId === header.id &&
                                    'w-auto opacity-100',
                                )}
                              >
                                <Button
                                  variant="ghost"
                                  size={'icon'}
                                  onClick={(e) => {
                                    if (manageColumnsOpen) {
                                      setManageColumnsOpen(false);
                                      e.stopPropagation();
                                    }
                                  }}
                                >
                                  <MoreVert className="h-4 w-4 text-gray-600" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    header.column.toggleVisibility(false);
                                  }}
                                >
                                  <EyeClosed className="h-4 w-4" /> Hide Column
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setTimeout(() => {
                                      setManageColumnsOpen({
                                        open: true,
                                        headerId: header.id,
                                      });
                                    }, 300);
                                  }}
                                >
                                  <ListSelect className="h-4 w-4" />
                                  Manage Columns
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </PopoverAnchor>
                          <ColumnManagementPopoverContent
                            table={table}
                            close={() => setManageColumnsOpen(false)}
                          />
                        </Popover>
                      )}

                    <div
                      {...{
                        onDoubleClick: () => header.column.resetSize(),
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `resizer ${
                          header.column.getIsResizing() ? 'isResizing' : ''
                        }`,
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* When resizing any column we will render this special memoized version of our table body */}
          {table.getState().columnSizingInfo.isResizingColumn ? (
            <MemoizedTableBody
              table={table}
              rowVirtualizer={rowVirtualizer}
              tableBodyClassName={tableBodyClassName}
              rowClassName={rowClassName}
              cellClassName={cellClassName}
              onRowClick={onRowClick}
              onCellClick={_onCellClick}
              virtualized={virtualized}
              loading={!data}
            />
          ) : (
            <TableBody
              table={table}
              rowVirtualizer={rowVirtualizer}
              tableBodyClassName={tableBodyClassName}
              rowClassName={rowClassName}
              cellClassName={cellClassName}
              onRowClick={onRowClick}
              onCellClick={_onCellClick}
              virtualized={virtualized}
              loading={!data}
            />
          )}
        </div>
      </div>
    );
  },
);

// un-memoized normal table body component - see memoized version below
function TableBody<T>({
  table,
  tableBodyClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}: {
  table: Table<T>;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  loading?: boolean;
} & (
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      virtualized: true;
    }
  | {
      virtualized?: false;
    }
)) {
  return (
    <div
      className={classNames('tbody', tableBodyClassName)}
      style={
        props.virtualized
          ? {
              height: `${props.rowVirtualizer.getTotalSize()}px`, // tells scrollbar how big the table is
              position: 'relative', // needed for absolute positioning of rows
            }
          : {}
      }
    >
      <TableBodyContent
        table={table}
        tableBodyClassName={tableBodyClassName}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        {...props}
      />
    </div>
  );
}

function TableBodyContent<T>({
  table,
  tableBodyClassName,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}: {
  table: Table<T>;
  tableBodyClassName?: string;
  rowClassName?: RowClassName<T>;
  cellClassName?: CellClassName<T>;
  onRowClick?: (row: T, e: MouseEvent) => void;
  onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
  loading?: boolean;
} & (
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      virtualized: true;
    }
  | {
      virtualized?: false;
    }
)) {
  if (props.virtualized) {
    return props.rowVirtualizer.getVirtualItems().map((virtualRow) => {
      const row = table.getRowModel().rows[virtualRow.index];
      return (
        <TableRow
          key={row.id}
          row={row}
          rowVirtualizer={props.rowVirtualizer}
          virtualRow={virtualRow}
          rowClassName={rowClassName}
          cellClassName={cellClassName}
          onRowClick={onRowClick}
          onCellClick={onCellClick}
          virtualized
          loading={props.loading}
        />
      );
    });
  }

  return table.getRowModel().rows.map((row) => {
    return (
      <TableRow
        key={row.id}
        row={row}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        loading={props.loading}
      />
    );
  });
}

// special memoized wrapper for our table body that we will use during column resizing
const MemoizedTableBody = React.memo(
  TableBody,
  (prev, next) => prev.table.options.data === next.table.options.data,
) as typeof TableBody;

function TableRow<T>({
  row,
  rowClassName,
  cellClassName,
  onRowClick,
  onCellClick,
  ...props
}:
  | {
      row: Row<T>;
      virtualRow: VirtualItem;
      rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
      rowClassName?: RowClassName<T>;
      cellClassName?: CellClassName<T>;
      onRowClick?: (row: T, e: MouseEvent) => void;
      onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
      loading?: boolean;
      virtualized: true;
    }
  | {
      row: Row<T>;
      rowClassName?: RowClassName<T>;
      cellClassName?: CellClassName<T>;
      onRowClick?: (row: T, e: MouseEvent) => void;
      onCellClick?: (cell: Cell<T, unknown>, e: MouseEvent) => void;
      loading?: boolean;
      virtualized?: false;
    }) {
  return (
    <div
      ref={(node) => {
        props.virtualized && props.rowVirtualizer.measureElement(node);
      }}
      key={row.id}
      className={classNames(
        'border-base-200 group/row border-b py-2',
        typeof rowClassName === 'function' ? rowClassName(row) : rowClassName,
      )}
      data-index={props.virtualized && props.virtualRow.index}
      style={
        props.virtualized
          ? {
              position: 'absolute',
              transform: `translateY(${props.virtualRow.start}px)`, // this should always be a `style` as it changes on scroll
              width: '100%',
            }
          : {}
      }
      onClick={(e) => !props.loading && onRowClick?.(row.original, e)}
    >
      <div className="tr group-hover/row:bg-base-100 rounded-lg">
        {row.getVisibleCells().map((cell) => {
          return (
            <div
              key={cell.id}
              className={classNames(
                'td text-base-900 min-h-12 items-center overflow-hidden px-5 py-1.5 text-sm whitespace-nowrap',
                cell.column.getIsPinned()
                  ? 'group-hover/row:bg-base-100 bg-white/90'
                  : undefined,
                cell.column.getIsPinned() === 'right' &&
                  cell.column.getIsLastColumn('right')
                  ? 'group-hover/row:rounded-e-lg'
                  : undefined,
                cell.column.getIsPinned() === 'left' &&
                  cell.column.getIsFirstColumn('left')
                  ? 'group-hover/row:rounded-s-lg'
                  : undefined,
                typeof cellClassName === 'function'
                  ? cellClassName(cell, row)
                  : cellClassName,
              )}
              style={{
                ...(cell.column.getIsPinned()
                  ? getCommonPinningStyles('cell', cell.column as any)
                  : {}),
                width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
              }}
              onClick={(e) => {
                !props.loading && onCellClick?.(cell, e);
              }}
            >
              {!props.loading &&
                flexRender(cell.column.columnDef.cell, cell.getContext())}
              {props.loading &&
                flexRender(() => <AnimateCell />, cell.getContext())}
            </div>
          );
        })}
      </div>
    </div>
  );
}
